// src/pages/OrderSummary.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

const OrderSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState(null);
  const [confirmationResponse, setConfirmationResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Extract orderId from URL query parameters.
  const params = new URLSearchParams(location.search);
  let orderIdFromUrl = params.get("orderId");

  // If orderId is not in the URL, fall back to localStorage.
  if (!orderIdFromUrl) {
    const storedOrderNumber = localStorage.getItem("orderNumber");
    if (storedOrderNumber) {
      //console.log("[OrderSummary] Falling back to localStorage orderNumber:", storedOrderNumber);
      orderIdFromUrl = storedOrderNumber;
    }
  }

  // Retrieve userId from localStorage.
  const authData = localStorage.getItem("authData")
    ? JSON.parse(localStorage.getItem("authData"))
    : null;
  const userId = authData?.user?.sub || null;

  useEffect(() => {
    if (!orderIdFromUrl) {
      setError("Order ID not found in URL or localStorage.");
      return;
    }
    setIsLoading(true);
    //console.log("[OrderSummary] Fetching order details with ID:", orderIdFromUrl);
    // Use a POST request to call the existingOrder endpoint.
    fetch(process.env.REACT_APP_EXISTING_ORDER_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderId: orderIdFromUrl }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Assume the API returns order details in data.body as a JSON string.
        const order = data.body ? JSON.parse(data.body) : data;
        //console.log("[OrderSummary] Fetched order details:", order);
        setOrderDetails(order);
        setIsLoading(false);
      })
      .catch((err) => {
        setError("Error fetching order details: " + err.message);
        setIsLoading(false);
      });
  }, [orderIdFromUrl]);

  useEffect(() => {
    // When order details and userId are available, confirm the order.
    if (orderDetails && userId) {
      // Use orderDetails.orderId if available, otherwise fall back to the one we used.
      const finalOrderId = orderDetails.orderId || orderIdFromUrl;
      // Build the confirmation payload.
      const payload = {
        orderId: finalOrderId,
        userId: userId,
        paymentIntent: orderDetails.paymentIntent, // expecting a string like "pi_xyz891"
        purchasedCredits: Number(orderDetails.credits) || 0,
      };

      //console.log("[OrderSummary] Confirm payload:", payload);

      fetch(process.env.REACT_APP_ORDER_SUMMARY_URL,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((data) => {
          //console.log("[OrderSummary] Response from confirm endpoint:", data);
          setConfirmationResponse(data);
        })
        .catch((err) => {
          //console.error("[OrderSummary] Error confirming order:", err);
          setError("Error confirming order: " + err.message);
        });
    }
  }, [orderDetails, userId, orderIdFromUrl]);

  return (
    <Layout>
      <section className="py-12 bg-white sm:py-16 lg:py-20">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="text-center">
              <p className="text-xl font-semibold">Loading order details...</p>
            </div>
          ) : error ? (
            <div className="text-center">
              <p className="text-xl font-semibold text-red-500">{error}</p>
            </div>
          ) : orderDetails ? (
            <div className="max-w-3xl mx-auto">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl xl:text-5xl">
                  Thank You For Your Purchase!
                </h1>
                <p className="mt-4 text-base font-normal text-gray-600">
                  Your payment has been successfully processed.
                </p>
              </div>

              <div className="mt-12 bg-white shadow rounded-lg">
                <div className="px-6 py-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">Order Summary</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    Order #{orderDetails.orderId || orderIdFromUrl} — Processed on{" "}
                    <span className="font-medium">{new Date().toLocaleDateString()}</span>
                  </p>
                </div>

                <div className="px-6 py-6 space-y-4">
                  <div className="grid grid-cols-2 gap-y-2">
                   

                   

                    <span className="text-sm font-medium text-gray-500">Amount</span>
                    <span className="text-sm font-bold text-gray-900">${orderDetails.amount}</span>

                    <span className="text-sm font-medium text-gray-500">Credits</span>
                    <span className="text-sm font-bold text-gray-900">{orderDetails.credits}</span>

                    <span className="text-sm font-medium text-gray-500">Auto Renew</span>
                    <span className="text-sm font-bold text-gray-900">
                      {orderDetails.autoRenew ? "Yes" : "No"}
                    </span>

                    <span className="text-sm font-medium text-gray-500">Option</span>
                    <span className="text-sm font-bold text-gray-900">{orderDetails.option}</span>

                   

                    
                  </div>
                </div>
              </div>

              {confirmationResponse && (
                <div className="mt-4 p-4 bg-green-100 text-green-800 rounded">
                  Order confirmed successfully.
                </div>
              )}

              <div className="mt-12 text-center">
                <button
                  onClick={() => navigate("/dash")}
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-bold text-gray-900 transition-all duration-200 bg-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 hover:bg-gray-100"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-xl font-semibold">No order details available.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default OrderSummary;
